import React from 'react';
import {
    ExtendedSelect,
    IExtendedSelectEvent,
} from 'components';
import { IExamApplication } from 'components/models';
import {
 MappedExamApplicationStatuses, ExamApplicationStatus, ExamStatus, ExamSessionReadyStatuses,
} from 'components/enums';
import { Table, FormControl } from 'react-bootstrap';
import { ColorStatus, Color, formatPhoneNumber } from '_helpers';
import Pagination, { IPaginationData } from '../Pagination/Pagination';
import styles from './ExamApplicationsTable.module.scss';
import sortIcon from '../assets/svg/sort_icon.svg';
import statusIcon from '../assets/svg/status_icon.svg';
import cn from 'classnames';
import './DatePickerFixStyles.scss';

const ExamApplicationsTable = (props: ExamApplicationsTableProps) => {
    const initialFilter: IFilter = {
        fieldName: '',
        fieldValue: '',
    };

    const [filter, setFilter] = React.useState(initialFilter);
    const [savedPagination, setSavedPagination] = React.useState(props.pagination);
    const [examApplications, setExamApplications] = React.useState(props.examApplications);
    const [applicantsStatuses, setApplicantsStatuses] = React.useState<{ label: string; value: string }[]>([]);
    const [selectedOption, setSelectedOption] = React.useState<IExtendedSelectEvent>({ name: '', value: '' });

    React.useEffect(() => {
        const applicantsStatusesArr: string[] = examApplications.map(e => e.status || 'undefined');

        const filteredApplicantsStatuses = applicantsStatusesArr.filter((e, i) => applicantsStatusesArr.indexOf(e) === i);
        const filteredApplicantsStatusesLength = filteredApplicantsStatuses.length;
        let statusOptions = [emptyOption];

        for (let i = 0; i < filteredApplicantsStatusesLength; i += 1) {
            const value = filteredApplicantsStatuses[i];

            if (value !== 'undefined') {
                statusOptions = [...statusOptions, {
                    label: MappedExamApplicationStatuses[value].text,
                    value,
                }];
            }
        }

        setApplicantsStatuses(statusOptions);
    }, []);

    React.useEffect(() => {
        setExamApplications(props.examApplications);
    }, [props.examApplications]);

    const emptyOption = {
        label: 'Все',
        value: 'undefined',
    };

    const isExpertOptions = [
        emptyOption,
        {
            label: 'Эксперт',
            value: 'true',
        },
        {
            label: 'Соискатель',
            value: 'false',
        },
    ];

    const finishedStatusOptions: IFinishedStatusOptions[] = [
        {
            label: 'Все',
            value: 'undefined',
        },
        {
            label: 'Экзамен обработан',
            value: 'processed',
        },
        {
            label: 'Финализировать практику',
            value: 'finalization',
        },
        {
            label: 'Написать рекомендацию',
            value: 'recommendation',
        },
    ];

    const triggerParentChange = (data?: IPaginationData, nFilter?: IFilter) => {
        const { fieldName, fieldValue } = nFilter || filter;

        if (data) {
            const { page, perPage } = data;

            setSavedPagination(data);
            props.onChange(fieldName, fieldValue, page, perPage);
        }

        props.onChange(fieldName, fieldValue, undefined, undefined);
    };

    const onFilterFieldChange = (e: any) => {
        setSelectedOption({ name: '', value: '' });
        const event = e as React.ChangeEvent<HTMLInputElement>;
        const { name } = event.target;
        const { value } = event.target;

        const newFilter = { ...filter };
        newFilter.fieldName = name;
        newFilter.fieldValue = value;

        setFilter(newFilter);
        triggerParentChange(props.pagination, newFilter);
    };

    const onHandleSelectChange = (e: IExtendedSelectEvent) => {
        setSelectedOption(e);
        const searchField = e.value !== 'undefined' ? e.name : undefined;
        const searchTerm = e.value !== 'undefined' ? e.value : undefined;
        props.onChange(searchField, searchTerm, savedPagination && savedPagination.page, savedPagination && savedPagination.perPage);
    };

    const renderApplicantStatus = (status: ExamApplicationStatus, examSessionReadyStatus?: ExamSessionReadyStatuses, finalized?: boolean) => {
        if (props.examStatus === ExamStatus.Finished && status !== ExamApplicationStatus.Absent && status !== ExamApplicationStatus.Canceled) {
            const colorStatus = finalized ? new ColorStatus(Color.Success, 'Экзамен обработан')
                : status === ExamApplicationStatus.Passed ? new ColorStatus(Color.Orange, 'Финализировать практику')
                    : new ColorStatus(Color.Orange, 'Написать рекомендацию');

            return (
                <td className="border-0" style={{ color: `var(${colorStatus.color})` }}>
                    {colorStatus.text}
                </td>
            );
        }

        const colorStatus = status === ExamApplicationStatus.Passed
            ? new ColorStatus(finalized ? Color.Primary : Color.Success, finalized ? 'Экзамен сдан' : 'Теория сдана')
            : status === ExamApplicationStatus.NotPassed && finalized ? new ColorStatus(Color.Primary, 'Не сдан')
                : status === ExamApplicationStatus.IsPassing
                    ? new ColorStatus(Color.Success, examSessionReadyStatus === ExamSessionReadyStatuses.TheoreticalExamIsPassingAndSessionStarted ? 'Идет сдача теории' : examSessionReadyStatus === ExamSessionReadyStatuses.PracticalExamIsPassingAndSessionStarted ? 'Сдача практики' : 'Перерыв')
                    : MappedExamApplicationStatuses[status] || new ColorStatus(Color.Default, status);

        return (
            <td className="border-0" style={{ color: `var(${colorStatus.color})` }}>
                {colorStatus.text}
            </td>
        );
    };

    const renderTableRow = ({
       examApplicationId, applicantFullName, applicantPhone, isExpert, status, examSessionReadyStatus, finalized,
    }: IExamApplication, i: number) => (
            <tr key={i} onClick={() => props.onItemClick && props.onItemClick(examApplicationId)}>
                <td className="border-0">
                    {applicantFullName}
                </td>
                <td className="border-0">
                    {applicantPhone ? (formatPhoneNumber(applicantPhone) || '') : ''}
                </td>
                <td className="border-0">
                    {isExpert ? 'Эксперт' : 'Соискатель'}
                </td>
                {renderApplicantStatus(status, examSessionReadyStatus, finalized)}
            </tr>
        );

    return (
        <div className={props.className}>
            <Table className="mb-3 w-100 advancedTable">
                <thead>
                    <tr>
                        {props.headers.map(({ value, key }, i: number) => {
                            let textValue = '';
                            if (key === filter.fieldName) {
                                textValue = filter.fieldValue;
                            }

                            return (
                                <th className={cn(styles.th, 'border-0')} key={`th_${i}_${key}`}>
                                    <div className={styles.table_header_text}>
                                        {value}
                                        <img src={sortIcon} alt="sortIcon" />
                                    </div>
                                    {key !== 'isExpert' && key !== 'status' ? (
                                        <FormControl
                                          name={key}
                                          placeholder="Искать"
                                          value={textValue}
                                          onChange={onFilterFieldChange}
                                        />
                                    ) : null}
                                    {key === 'isExpert' ? (
                                        <ExtendedSelect
                                          name="isExpert"
                                          value={(selectedOption.name === 'isExpert' ? selectedOption.value : emptyOption.value) || (props.filters && `${props.filters.isExpert}`)}
                                          options={isExpertOptions}
                                          placeholder="Выбрать"
                                          className={styles.selectOptions}
                                          onChange={onHandleSelectChange}
                                        />
                                    ) : null}
                                    {key === 'status' ? (
                                        <ExtendedSelect
                                          name="status"
                                          value={(selectedOption.name === 'status' ? selectedOption.value : emptyOption.value) || (props.filters && `${props.filters.status}`)}
                                          options={props.examStatus !== ExamStatus.Finished ? applicantsStatuses : finishedStatusOptions}
                                          placeholder="Выбрать"
                                          className={styles.statusSelectOptions}
                                          onChange={onHandleSelectChange}
                                        />
                                    ) : null}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className={styles.table__body}>
                    {!examApplications.length && (
                        <tr>
                            <td colSpan={props.headers.length} className="text-center">Записи не найдены</td>
                        </tr>
                    )}
                    {examApplications.map(renderTableRow)}
                </tbody>
            </Table>
            {props.pagination && <Pagination className={props.paginationClassName} pagination={props.pagination} onChange={triggerParentChange} />}
        </div>
    );
};

interface ExamApplicationsTableProps {
    headers: ExamApplicationsColumnHeader[];
    pagination?: IPaginationData;
    examApplications: IExamApplication[];
    examStatus: ExamStatus;
    className?: string;
    paginationClassName?: string;
    withoutIcons?: boolean;
    filters?: { [key: string]: string };
    onChange: (searchField: string | undefined, searchTerm: string | undefined, pageNumber: number | undefined, pageSize: number | undefined) => void;
    onItemClick?: (id: string) => void;
}

interface IFilter {
    fieldName: string;
    fieldValue: string;
}

interface ColumnHeader {
    value: string;
    key: string;
}

export class ExamApplicationsColumnHeader implements ColumnHeader {
    value: string;
    key: keyof IExamApplication;

    constructor(key: keyof IExamApplication, value: string) {
        this.key = key;
        this.value = value;
    }
}

interface IFinishedStatusOptions {
    label: string;
    value: 'processed' | 'finalization' | 'recommendation' | 'undefined';
}

export default ExamApplicationsTable;
